"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Archivo } from "next/font/google";
import { ChevronLeft, ChevronRight } from "lucide-react";

const archivoSemiBold = Archivo({
  subsets: ["latin"],
  weight: "500",
});

type SocialPlatform = "youtube" | "tiktok" | "instagram" | "twitch";

interface TalentEntry {
  nom: string;
  url: string;
  at_yt: string;
  twitter: string;
  twitch: string;
  instagram: string;
  img?: string;
  followers?: string;
  avatar?: string;
  latestVideos?: VideoPreview[];
  video?: string;
  hoverVideo?: string;
  pct_male?: string;
  pct_female?: string;
  pct_french?: string;
  pct_plus_25_yo?: string;
}

interface VideoPreview {
  title: string;
  url: string;
  thumbnail: string;
}

interface RosterMember {
  slug: string;
  name: string;
  primaryUrl: string;
  youtubeHandle?: string;
  followers?: string;
  avatar?: string;
  hoverVideo?: string;
  latestVideos: VideoPreview[];
  pctMale?: string;
  pctFemale?: string;
  pctFrench?: string;
  pctPlus25Yo?: string;
  socials: { platform: SocialPlatform; url: string }[];
}

const toRosterMember = (talent: TalentEntry): RosterMember => {
  const socials: { platform: SocialPlatform; url: string }[] = [
    { platform: "youtube", url: talent.url?.trim() || "" },
    { platform: "tiktok", url: talent.twitter?.trim() || "" },
    { platform: "instagram", url: talent.instagram?.trim() || "" },
    { platform: "twitch", url: talent.twitch?.trim() || "" },
  ];

  const slug = talent.nom
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return {
    slug,
    name: talent.nom,
    primaryUrl: talent.url || "#",
    youtubeHandle: talent.at_yt || "",
    followers: talent.followers || "",
    avatar: talent.img || talent.avatar || "",
    hoverVideo: talent.hoverVideo || talent.video || "",
    latestVideos: talent.latestVideos || [],
    pctMale: talent.pct_male || "",
    pctFemale: talent.pct_female || "",
    pctFrench: talent.pct_french || "",
    pctPlus25Yo: talent.pct_plus_25_yo || "",
    socials,
  };
};

const CARDS_PER_PAGE = 4;
const SLIDE_MS = 700;

const iconMap: Record<SocialPlatform, { src: string; alt: string }> = {
  youtube: { src: "/reseaux/yt.png", alt: "YouTube" },
  tiktok: { src: "/reseaux/tiktok.png", alt: "TikTok" },
  instagram: { src: "/reseaux/insta.png", alt: "Instagram" },
  twitch: { src: "/reseaux/twitch.png", alt: "Twitch" },
};

/* ---- Talent card with hover: scale + shadows + video overlay + bottom info bar ---- */
function TalentCard({ member, onSelect }: { member: RosterMember; onSelect: (slug: string, openInNewTab?: boolean) => void }) {
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  /* Play / pause hover video */
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (hovered) {
      vid.currentTime = 0;
      vid.play().catch(() => {});
    } else {
      vid.pause();
    }
  }, [hovered]);

  return (
    <div
      className={`group text-center cursor-pointer transition-transform duration-500 ease-out origin-center ${
        hovered ? "scale-105 z-20" : "scale-100 z-0"
      }`}
      onClick={(event) => {
        const openInNewTab = event.button === 0 && (event.ctrlKey || event.metaKey);
        onSelect(member.slug, openInNewTab);
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(member.slug);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Image container */}
      <div className="relative w-full aspect-[520/754] overflow-visible">
        {/* External side shadows — visible on hover */}
        <div
          className={`absolute inset-y-4 -left-6 w-10 pointer-events-none z-10 bg-gradient-to-r from-black/80 to-transparent blur-md transition-opacity duration-300 ${
            hovered ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`absolute inset-y-4 -right-6 w-10 pointer-events-none z-10 bg-gradient-to-l from-black/80 to-transparent blur-md transition-opacity duration-300 ${
            hovered ? "opacity-100" : "opacity-0"
          }`}
        />

        <div className="absolute inset-0 overflow-hidden bg-surface-light rounded-2xl">
          {/* Base image — grayscale, scales up on hover */}
          {member.avatar ? (
            <img
              src={member.avatar}
              alt={member.name}
              className="absolute inset-0 w-full h-full object-cover grayscale"
              loading="lazy"
            />
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-surface-lighter to-surface-light" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-accent-light/30">
                  {member.name.split(" ").pop()}
                </span>
              </div>
            </>
          )}

          {/* Video overlay — full color, shown on hover */}
          {member.hoverVideo && (
            <video
              ref={videoRef}
              src={member.hoverVideo}
              muted
              loop
              playsInline
              className={`absolute inset-0 w-full h-full object-cover z-20 transition-opacity duration-1000 ${
                hovered ? "opacity-100" : "opacity-0"
              }`}
            />
          )}

          {/* Bottom gradient overlay with social icons on image */}
          <div
            className={`absolute bottom-0 left-0 right-0 z-30 px-4 pb-4 pt-16 transition-opacity duration-300 ${
              hovered ? "opacity-100" : "opacity-0"
            }`}
            style={{
              background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.85) 100%)",
            }}
          >
            <div className="flex justify-center gap-2 mt-2">
              {member.socials.map((social) => {
                const icon = iconMap[social.platform];

                if (!social.url) {
                  return (
                    <span
                      key={social.platform}
                      className="w-10 h-10 flex items-center justify-center opacity-50"
                      aria-label={social.platform}
                    >
                      <Image src={icon.src} alt={icon.alt} width={20} height={20} className="object-contain" />
                    </span>
                  );
                }

                return (
                  <a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="group w-10 h-10 flex items-center justify-center transition-all duration-300"
                    aria-label={social.platform}
                  >
                    <Image src={icon.src} alt={icon.alt} width={20} height={20} className="object-contain" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Name below card */}
      <span className="inline-block font-semibold text-white text-base mt-4">
        {member.name}
      </span>

      {member.followers && (
        <p className="text-muted text-sm mt-0.5">
          {member.followers} abonnés
        </p>
      )}
    </div>
  );
}

export default function RosterSection() {
  const router = useRouter();
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [page, setPage] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const [visibleCards, setVisibleCards] = useState(4);
  const [trackIndex, setTrackIndex] = useState(0);
  const [trackTransitionEnabled, setTrackTransitionEnabled] = useState(true);
  const slideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onLoad = () => setIsLoaded(true);

    if (document.readyState === "complete") {
      onLoad();
      return;
    }

    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  useEffect(() => {
    const loadTalents = async () => {
      const pathPrefix = typeof window !== "undefined"
        ? window.location.pathname.split("/").filter(Boolean)[0]
        : "";

      const candidates = [
        "/api/talents",
        pathPrefix ? `/${pathPrefix}/api/talents` : "",
      ].filter(Boolean);

      try {
        for (const url of candidates) {
          const response = await fetch(url);
          if (!response.ok) continue;

          const talents = (await response.json()) as TalentEntry[];
          if (!Array.isArray(talents)) continue;

          setRoster(talents.map(toRosterMember));
          return;
        }

        setRoster([]);
      } catch {
        setRoster([]);
      }
    };

    loadTalents();
  }, []);

  useEffect(() => {
    return () => {
      if (slideTimerRef.current !== null) window.clearTimeout(slideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const syncVisibleCards = () => {
      setVisibleCards(window.innerWidth < 768 ? 2 : CARDS_PER_PAGE);
    };

    syncVisibleCards();
    window.addEventListener("resize", syncVisibleCards);
    return () => window.removeEventListener("resize", syncVisibleCards);
  }, []);

  const canSlide = roster.length > visibleCards;
  const extendedRoster = canSlide
    ? [
        ...roster.slice(-visibleCards),
        ...roster,
        ...roster.slice(0, visibleCards),
      ]
    : roster;

  useEffect(() => {
    setPage(0);
    setTrackTransitionEnabled(true);
    setTrackIndex(canSlide ? visibleCards : 0);
    setIsSliding(false);
    if (slideTimerRef.current !== null) {
      window.clearTimeout(slideTimerRef.current);
      slideTimerRef.current = null;
    }
  }, [roster.length, visibleCards, canSlide]);

  const finishSlideWithWrap = (nextTrackIndex: number) => {
    if (!canSlide || roster.length === 0) {
      setIsSliding(false);
      return;
    }

    const minIndex = visibleCards;
    const maxIndex = visibleCards + roster.length - 1;

    if (nextTrackIndex < minIndex) {
      setTrackTransitionEnabled(false);
      setTrackIndex(maxIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTrackTransitionEnabled(true));
      });
    } else if (nextTrackIndex > maxIndex) {
      setTrackTransitionEnabled(false);
      setTrackIndex(minIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTrackTransitionEnabled(true));
      });
    }

    setIsSliding(false);
  };

  const slideBy = (step: -1 | 1) => {
    if (isSliding || roster.length === 0 || !canSlide) return;

    const nextPage = (page + step + roster.length) % roster.length;
    const nextTrackIndex = trackIndex + step;

    setIsSliding(true);
    setPage(nextPage);
    setTrackIndex(nextTrackIndex);

    if (slideTimerRef.current !== null) window.clearTimeout(slideTimerRef.current);

    slideTimerRef.current = window.setTimeout(() => {
      finishSlideWithWrap(nextTrackIndex);
    }, SLIDE_MS);
  };

  const goLeft = () => slideBy(-1);
  const goRight = () => slideBy(1);
  const redirectToTalent = (slug: string, openInNewTab = false) => {
    const targetUrl = `/talents#${slug}`;

    if (openInNewTab) {
      const pathSegments = window.location.pathname.split("/").filter(Boolean);
      const talentsIndex = pathSegments.indexOf("talents");
      const baseSegments = talentsIndex >= 0 ? pathSegments.slice(0, talentsIndex) : pathSegments.slice(0, 1);
      const basePath = baseSegments.length ? `/${baseSegments.join("/")}` : "";
      const prefixedTargetUrl = `${basePath}/talents#${slug}`;

      window.open(prefixedTargetUrl, "_blank", "noopener,noreferrer");
      return;
    }

    router.push(targetUrl);
  };

  const redirectToTalents = () => {
    router.push("/talents");
  };

  return (
    <section
      id="talents"
      className={`pb-24 bg-surface overflow-hidden transition-opacity duration-500 ${
        isLoaded ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 h-40 flex items-center justify-center text-center">
        <h2 className={`${archivoSemiBold.className} text-white uppercase tracking-[0.3em] text-2xl`}>
          Talents
        </h2>
      </div>

      {/* Carousel — full viewport width */}
      <div className="relative w-screen left-1/2 -translate-x-1/2 px-4 md:px-8">
        {/* Left arrow */}
        <button
          onClick={goLeft}
          className="group absolute left-0 md:left-2 top-[45%] -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-surface-lighter/80 backdrop-blur hover:bg-white flex items-center justify-center transition-all duration-300 cursor-pointer disabled:opacity-50"
          disabled={isSliding || !canSlide}
          aria-label="Précédent"
        >
          <ChevronLeft size={24} className="text-white group-hover:text-black transition-colors duration-300" />
        </button>

        {/* Right arrow */}
        <button
          onClick={goRight}
          className="group absolute right-0 md:right-2 top-[45%] -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-surface-lighter/80 backdrop-blur hover:bg-white flex items-center justify-center transition-all duration-300 cursor-pointer disabled:opacity-50"
          disabled={isSliding || !canSlide}
          aria-label="Suivant"
        >
          <ChevronRight size={24} className="text-white group-hover:text-black transition-colors duration-300" />
        </button>

        {/* Cards — smooth track */}
        <div className="relative overflow-hidden">
          <div
            className={`flex will-change-transform ${
              trackTransitionEnabled
                ? "transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                : ""
            }`}
            style={{
              transform: `translateX(-${trackIndex * (100 / Math.max(visibleCards, 1))}%)`,
            }}
          >
            {extendedRoster.map((member, index) => (
              <div
                key={`${member.name}-${index}`}
                style={{ flex: `0 0 ${100 / Math.max(visibleCards, 1)}%` }}
                className="px-2"
              >
                <TalentCard member={member} onSelect={redirectToTalent} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Talents CTA */}
      <div className="w-screen relative left-1/2 -translate-x-1/2 mt-15 px-2 md:px-2">
        <div className="flex justify-center">
          <button
            onClick={redirectToTalents}
            className={`${archivoSemiBold.className} bg-white text-black px-8 py-3 rounded-full text-sm uppercase tracking-wide hover:bg-white/90 transition-colors duration-300 cursor-pointer`}
          >
            Tous nos talents
          </button>
        </div>
      </div>
    </section>
  );
}
